import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { TheraHeaderActionButtonComponent } from "src/app/common/components/core/header-action-button/header-action-button.component";
import { Announcement, AnnouncementPanelService } from "./announcement-panel.service";
import {
  SEVERITY_STYLES,
  MAX_VISIBLE_BANNERS,
  ANNOUNCEMENT_BANNER_TEXT,
} from "./announcement-banner-constants";

@Component({
  selector: "app-announcement-banner",
  standalone: true,
  imports: [CommonModule, FormsModule, TheraHeaderActionButtonComponent],
  templateUrl: "./announcement-banner.component.html",
  styleUrls: ["./announcement-banner.component.scss"],
})
export class AnnouncementBannerComponent implements OnInit, OnDestroy {
  active: Announcement[] = [];
  dontShowAgain = false;
  severityStyles = SEVERITY_STYLES;
  text = ANNOUNCEMENT_BANNER_TEXT;

  /** "list" = the bell/"+N more" drawer showing every active announcement as
   * a card; "detail" = the original single-item slideout (full content). */
  panelOpen = false;
  panelMode: "list" | "detail" = "list";
  selectedId: string | null = null;

  private readonly subs: Subscription[] = [];

  constructor(private panelService: AnnouncementPanelService) {}

  ngOnInit(): void {
    this.subs.push(
      this.panelService.active$.subscribe((active) => (this.active = active)),
      this.panelService.panelOpen$.subscribe((open) => (this.panelOpen = open)),
      this.panelService.panelMode$.subscribe((mode) => (this.panelMode = mode)),
      this.panelService.selectedId$.subscribe((id) => (this.selectedId = id))
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  get visibleBanners(): Announcement[] {
    return this.active.slice(0, MAX_VISIBLE_BANNERS);
  }

  get overflowCount(): number {
    return this.active.length - this.visibleBanners.length;
  }

  get selected(): Announcement | null {
    return this.active.find((a) => a.id === this.selectedId) ?? null;
  }

  dismiss(id: string): void {
    this.panelService.dismiss(id);
  }

  /** Clicking a visible banner bar — jumps straight to that announcement's
   * full content, same as before this feature grew a list view. */
  openDetail(announcement: Announcement): void {
    this.dontShowAgain = false;
    this.panelService.openDetail(announcement.id);
  }

  /** "+N more" pill — opens the card list. The bell itself now lives in
   * SidenavComponent (see AnnouncementBellComponent), but both call the same
   * shared service method. */
  openList(): void {
    this.panelService.openList();
  }

  selectFromList(id: string): void {
    this.dontShowAgain = false;
    this.panelService.selectFromList(id);
  }

  backToList(): void {
    this.dontShowAgain = false;
    this.panelService.backToList();
  }

  closePanel(): void {
    this.panelService.closePanel(this.dontShowAgain);
    this.dontShowAgain = false;
  }
}
